import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
} from '@/components/ui/sidebar';
import { TeamSwitcher } from '@/components/layout/team-switcher';
import { sidebarData } from './data/sidebar-data';
import { Button } from '@/components/ui/button';
import { SchedulePostButton } from '../schedule-post-button';
import { useYoutubeConnection } from '@/hooks/useYoutubeConnection';
import YoutubeLogo from '@/assets/yt-logo.svg';
import { useAuth } from '@/features/auth/auth-context';
import { Check, X, Gauge, Video } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

export function AppSidebar({
  onScheduleClick,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  onScheduleClick: () => void;
}) {
  const { connected, connect, disconnect } = useYoutubeConnection();
  const { user } = useAuth();
  const location = useLocation();
  
  // State for YouTube channel info
  const [channelInfo, setChannelInfo] = useState<{
    name: string;
    avatar: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 5; // 5 retries = 15 seconds total

  const isActive = (path: string) => location.pathname === path;

  // Fetch YouTube channel info when connected
  useEffect(() => {
    const fetchChannelInfo = async () => {
      if (!connected || !user) {
        console.log('[Sidebar] Clearing channel info');
        setChannelInfo(null);
        setLoading(false);
        setRetryCount(0);
        return;
      }

      setLoading(true);
      console.log('[Sidebar] Fetching channel info for user:', user.id, `(attempt ${retryCount + 1}/${MAX_RETRIES})`);

      try {
        const timestamp = Date.now();
        // Get user's active channel first
        const { data: tokenData } = await supabase
          .from('youtube_tokens')
          .select('channel_id')
          .eq('user_id', user.id)
          .single();

        if (!tokenData?.channel_id) {
          console.log('[Sidebar] No channel connected');
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('channel_analytics')
          .select('channel_name, channel_avatar')
          .eq('user_id', user.id)
          .eq('youtube_channel_id', tokenData.channel_id)
          .maybeSingle();

        console.log('[Sidebar] Query result:', { data, error, timestamp });

        if (error && error.code !== 'PGRST116') {
          console.error('[Sidebar] Failed to fetch channel info:', error);
          setLoading(false);
          return;
        }

        if (data && data.channel_name && data.channel_avatar) {
          const info = {
            name: data.channel_name,
            avatar: data.channel_avatar,
          };
          console.log('[Sidebar] Setting channel info:', info);
          setChannelInfo(info);
          setRetryCount(0); // Reset on success
        } else {
          console.log('[Sidebar] No channel data found yet, waiting for sync...');
          
          // Only retry if under max retries
          if (retryCount < MAX_RETRIES) {
            console.log(`[Sidebar] Will retry in 3 seconds (${retryCount + 1}/${MAX_RETRIES})`);
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              setRefreshKey(prev => prev + 1);
            }, 3000);
          } else {
            console.warn('[Sidebar] Max retries reached, showing fallback');
            // Show fallback after max retries
            setChannelInfo({
              name: 'YouTube Channel',
              avatar: YoutubeLogo
            });
          }
        }
      } catch (err) {
        console.error('[Sidebar] Error fetching channel info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChannelInfo();
  }, [connected, user, refreshKey]);

  // Custom disconnect handler
  const handleDisconnect = async () => {
    await disconnect();
    setChannelInfo(null);
    setRefreshKey(0);
    setRetryCount(0);
  };

  // Debug log
  console.log('[Sidebar] Render state:', {
    connected,
    hasUser: !!user,
    hasChannelInfo: !!channelInfo,
    channelInfo,
    loading,
    refreshKey,
    retryCount
  });

  return (
    <Sidebar collapsible="none" variant="floating" {...props}>
      <SidebarHeader>
        <div className="flex flex-col gap-6">
          <TeamSwitcher teams={sidebarData.teams} />
          
          {/* Schedule Post CTA */}
          <SchedulePostButton 
            variant="sidebar"
            onAllowSchedule={onScheduleClick}
          />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Create Section */}
        <div className="mt-6 px-2">
          <h4 className="text-sm text-muted-foreground mb-2">
            Create
          </h4>
          
          <div className="space-y-1">
            {/* Dashboard Link */}
            <Link to="/">
              <Button
                variant="ghost"
                className={`w-full justify-start gap-4 ${
                  isActive('/')
                    ? 'bg-accent text-white'
                    : 'text-muted-foreground hover:text-white'
                }`}
                size="sm"
              >
                <Gauge className="w-4 h-4 text-white" />
                <span className="text-sm text-white">Dashboard</span>
              </Button>
            </Link>

            {/* Your Videos Link */}
            <Link to="/your-videos">
              <Button
                variant="ghost"
                className={`w-full justify-start gap-4 ${
                  isActive('/your-videos')
                    ? 'bg-accent text-white'
                    : 'text-muted-foreground hover:text-white'
                }`}
                size="sm"
              >
                <Video className="w-4 h-4 text-white" />
                <span className="text-sm text-white">Your Videos</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Social Accounts Section */}
        <div className="mt-6 px-2">
          <h4 className="text-sm text-muted-foreground mb-2">
            Social Accounts
          </h4>

          {/* Debug info in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-2 p-2 bg-white/5 rounded text-xs text-white/60">
              Connected: {connected ? 'Yes' : 'No'}<br/>
              User: {user ? 'Yes' : 'No'}<br/>
              Channel Info: {channelInfo ? 'Yes' : 'No'}<br/>
              Loading: {loading ? 'Yes' : 'No'}<br/>
              Refresh Key: {refreshKey}<br/>
              Retry Count: {retryCount}/{MAX_RETRIES}
            </div>
          )}

          {connected && user ? (
            // Show loading state while fetching channel info
            loading ? (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/5">
                <div className="w-8 h-8 rounded-full bg-white/10 animate-pulse" />
                <div className="flex flex-col gap-1 flex-1">
                  <div className="h-3 w-20 bg-white/10 rounded animate-pulse" />
                  <div className="h-2 w-16 bg-white/10 rounded animate-pulse" />
                </div>
              </div>
            ) : channelInfo ? (
              // CONNECTED STATE with channel info
              <button
                onClick={handleDisconnect}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors group"
              >
                {/* Channel Avatar with Badge */}
                <div className="relative flex-shrink-0">
                  <img 
                    src={channelInfo.avatar} 
                    alt={channelInfo.name}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = YoutubeLogo;
                    }}
                  />
                  
                  {/* Green check badge */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-sidebar flex items-center justify-center">
                    <Check className="w-2 h-2 text-white" strokeWidth={3} />
                  </div>
                </div>
                
                {/* Channel Name + Status */}
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span className="text-white text-sm font-normal truncate w-full text-left">
                    {channelInfo.name}
                  </span>
                  <span className="text-white/60 text-xs">
                    <span className="group-hover:hidden">Connected</span>
                    <span className="hidden group-hover:inline">Disconnect</span>
                  </span>
                </div>
                
                {/* Hover indicator */}
                <X className="w-4 h-4 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            ) : (
              // Still loading after connection - show loading state
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/5">
                <div className="w-8 h-8 rounded-full bg-white/10 animate-pulse" />
                <div className="flex flex-col gap-1 flex-1">
                  <div className="h-3 w-20 bg-white/10 rounded animate-pulse" />
                  <div className="h-2 w-16 bg-white/10 rounded animate-pulse" />
                </div>
              </div>
            )
          ) : (
            // DISCONNECTED STATE
            <Button
              onClick={connect}
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-white gap-4"
              size="sm"
            >
              <img src={YoutubeLogo} className="w-4 h-4" alt="YouTube" />
              <span className="text-sm">Connect YouTube</span>
            </Button>
          )}
        </div>
      </SidebarContent>
    </Sidebar>
  );
}